import type { Annotation, AttachmentDetails, Evidence, EvidenceSessionAIModel } from 'api';
import { logger } from 'monitor';
import { useState } from 'react';

import { useManageEvidence } from '../../../evidence';
import type { PreviewGalleryImage } from '../../types';
import { getPreviewImage } from '../../utils/getPreviewImage/getPreviewImage';

export const useManagePreviews = ({ onDeleteSuccess }: { onDeleteSuccess?: () => void } = {}) => {
  const [previewImages, setPreviewImages] = useState<PreviewGalleryImage[]>([]);

  const { deleteEvidenceFile } = useManageEvidence({
    onSuccess: () => {
      onDeleteSuccess?.();
    },
    showNotification: false,
  });

  const addImage = async ({ photo, isOnline }: { photo: File; isOnline: boolean }) => {
    const previewImage = await getPreviewImage({
      disableDelete: true,
      feedbackMessages: [],
      file: photo,
      message: isOnline ? 'Analyzing photo...' : 'Stored offline',
      status: isOnline ? 'uploading' : 'offline',
    });
    setPreviewImages((prev) => [...prev, previewImage]);

    return previewImage;
  };

  const updateOnlineImage = ({
    previewImage,
    accepted,
    imageRejectionReasons,
    url,
    attachmentDetails,
    targetWeightPercentage,
    aiModel,
    annotations,
  }: {
    previewImage: PreviewGalleryImage;
    accepted?: boolean;
    imageRejectionReasons: string[];
    url: string;
    attachmentDetails?: AttachmentDetails;
    targetWeightPercentage?: number;
    aiModel?: EvidenceSessionAIModel;
    annotations?: Annotation[];
  }) => {
    const originalSrc = previewImage.src;

    previewImage.status = accepted ? 'success' : 'warning';
    previewImage.feedbackMessages = imageRejectionReasons;
    previewImage.message = accepted ? 'Image successfully analysed!' : 'Photo failed to be AI analysed due to:';
    previewImage.disableDelete = false;
    previewImage.src = url;
    previewImage.attachmentDetails = attachmentDetails;
    previewImage.targetWeightPercentage = targetWeightPercentage;
    previewImage.aiModel = aiModel;
    previewImage.annotations = annotations;

    logger.info('Updated online image', {
      accepted,
      aiModel,
      attachmentDetails,
      imageRejectionReasons,
      previewImage,
      targetWeightPercentage,
      url,
    });

    setPreviewImages((prev) => prev.map((i) => (i.src === originalSrc ? previewImage : i)));
  };

  const updateOfflineImage = ({
    previewImage,
    id,
    status,
    message,
  }: {
    previewImage: PreviewGalleryImage;
    id: string;
    status?: PreviewGalleryImage['status'];
    message?: string;
  }) => {
    previewImage.id = id;
    previewImage.status = status || previewImage.status;
    previewImage.message = message || previewImage.message;
    previewImage.disableDelete = false;
    setPreviewImages((prev) => prev.map((i) => (i.src === previewImage.src ? previewImage : i)));
  };

  const handleDeletePreviewImage = ({
    image,
    isOnline,
    evidence,
    removeEvidenceFile,
  }: {
    image: PreviewGalleryImage;
    isOnline: boolean;
    evidence: Evidence[];
    removeEvidenceFile: (id: string) => void;
  }) => {
    setPreviewImages((prev) => prev.filter((i) => i.src !== image.src));

    if (!isOnline && image.id) {
      removeEvidenceFile(image.id);
      return;
    }

    const evidenceItem = evidence?.find((e) => e.file === image.src);
    if (!evidenceItem) {
      return;
    }

    deleteEvidenceFile(evidenceItem.id);
  };

  const resetSelectedImages = () => {
    setPreviewImages([]);
  };

  return {
    addImage,
    handleDeletePreviewImage,
    previewImages,
    resetSelectedImages,
    updateOfflineImage,
    updateOnlineImage,
  };
};
